import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import CustomMUITable from "src/components/CustomMUITable";
import Label from "src/components/Label";
import { buildTrackerDashboardStats } from "./trackerDashboardStats";
import {
  getInitials,
  getLeftByDisplay,
  getSubmissionPersonKey,
  isBmsMemberSource,
  loadSubmissionsFromApi,
} from "./submissionStorage";
import SubmissionNotesDialog, {
  NotesCountButton,
  notesForSubmissions,
  useTrackerSubmissionNotes,
} from "./SubmissionNotesDialog";
import { isLeadCaptureTracker } from "./trackerStorage";
import DropOutPersonDialog from "./DropOutPersonDialog";
import StaffCheckInDialog from "./StaffCheckInDialog";
import CoachProgressReportDialog from "./CoachProgressReportDialog";
import { withBack } from "./trackerNav";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";

const ACTIVITY_META = {
  active: { label: "Active", color: "success" },
  cooling: { label: "Cooling", color: "warning" },
  at_risk: { label: "Quiet", color: "error" },
  completed: { label: "Completed", color: "info" },
  dropped: { label: "Dropped", color: "default" },
  left: { label: "Left", color: "default" },
  unknown: { label: "—", color: "default" },
};

const VERDICT_META = {
  up: {
    label: "Improving",
    color: "#047857",
    bg: "rgba(16,185,129,0.12)",
    Icon: TrendingUpIcon,
  },
  down: {
    label: "Declining",
    color: "#b91c1c",
    bg: "rgba(239,68,68,0.12)",
    Icon: TrendingDownIcon,
  },
  mixed: {
    label: "Mixed",
    color: "#b45309",
    bg: "rgba(245,158,11,0.14)",
    Icon: TrendingFlatIcon,
  },
  flat: {
    label: "Stable",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
  },
  none: {
    label: "—",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
  },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

const StatCard = ({ label, value, hint, tone }) => (
  <Box
    sx={{
      flex: "1 1 120px",
      minWidth: 110,
      p: 1.5,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      borderLeft: tone ? `3px solid ${tone}` : undefined,
      bgcolor: "#fff",
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: MUTED }}>
      {label}
    </Typography>
    <Typography
      sx={{ mt: 0.4, fontSize: 22, fontWeight: 700, color: tone || "#0f172a" }}
    >
      {value}
    </Typography>
    {hint ? (
      <Typography sx={{ mt: 0.35, fontSize: 11, color: MUTED }}>{hint}</Typography>
    ) : null}
  </Box>
);

export default function TrackerDetailPeopleTab({ tracker }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dropPerson, setDropPerson] = useState(null);
  const [checkInPerson, setCheckInPerson] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportInitialMembers, setReportInitialMembers] = useState([]);
  const isLead = isLeadCaptureTracker(tracker);
  const trackerId = tracker?.id || tracker?._id;

  // Your private notes on this journey's submissions → per person (a person
  // can have notes on several of their submissions).
  const { bySubmission: notesBySubmission, reload: reloadNotes } =
    useTrackerSubmissionNotes(trackerId);
  const [notesPerson, setNotesPerson] = useState(null);
  const submissionIdsByPerson = useMemo(() => {
    const map = new Map();
    submissions.forEach((row) => {
      const key = getSubmissionPersonKey(row);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(String(row.id || row._id));
    });
    return map;
  }, [submissions]);
  const personNotes = (key) =>
    notesForSubmissions(notesBySubmission, submissionIdsByPerson.get(key) || []);
  const trackerName = tracker?.name || "";

  const reloadPeople = async () => {
    if (!trackerId) {
      setSubmissions([]);
      return;
    }
    const result = await loadSubmissionsFromApi(trackerId, { limit: 500 });
    setSubmissions(result.rows || []);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!trackerId) {
        setSubmissions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const result = await loadSubmissionsFromApi(trackerId, { limit: 500 });
      if (!cancelled) {
        setSubmissions(result.rows || []);
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [trackerId]);

  const stats = useMemo(() => {
    if (!trackerId) return null;
    return buildTrackerDashboardStats({
      trackers: [tracker],
      submissions,
    });
  }, [tracker, trackerId, submissions]);

  const clients = stats?.clients || [];
  const kpis = stats?.kpis || {};

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      `${c.name} ${c.email} ${c.coachName} ${c.statusName} ${
        c.performance?.label || ""
      }`
        .toLowerCase()
        .includes(q)
    );
  }, [clients, search]);

  const TABLE_HEAD = isLead
    ? [
        {
          id: "action",
          label: "Action",
          type: "action",
          MENU_OPTIONS: "MENU_OPTIONS",
        },
        { id: "person", label: "Lead", sortValue: (row) => row._raw?.name || "" },
        {
          id: "status",
          label: "Status",
          sortValue: (row) => row._raw?.statusName || "",
        },
        {
          id: "coach",
          label: "Coach",
          sortValue: (row) => row._raw?.coachName || "",
        },
        {
          id: "source",
          label: "Source",
          sortValue: (row) =>
            isBmsMemberSource(row._raw?.source) ? "Public form" : "GymRite member",
        },
        {
          id: "submitted",
          label: "Submitted",
          sortValue: (row) => row._raw?.lastAt || "",
        },
        {
          id: "notes",
          label: "Notes",
          sortValue: (row) => personNotes(row._raw?.key).length,
        },
      ]
    : [
        {
          id: "action",
          label: "Action",
          type: "action",
          MENU_OPTIONS: "MENU_OPTIONS",
        },
        { id: "person", label: "Person", sortValue: (row) => row._raw?.name || "" },
        {
          id: "performance",
          label: "Performance",
          sortValue: (row) =>
            (VERDICT_META[row._raw?.performance?.verdict] || VERDICT_META.none)
              .label,
        },
        {
          id: "activity",
          label: "Activity",
          sortValue: (row) =>
            (ACTIVITY_META[row._raw?.activity] || ACTIVITY_META.unknown).label,
        },
        {
          id: "progress",
          label: "Journey %",
          sortValue: (row) => Number(row._raw?.progress ?? 0),
        },
        {
          id: "coach",
          label: "Coach",
          sortValue: (row) => row._raw?.coachName || "",
        },
        {
          id: "entries",
          label: "Entries",
          alignRight: true,
          sortValue: (row) => Number(row._raw?.submissions ?? 0),
        },
        {
          id: "last",
          label: "Last check-in",
          sortValue: (row) => row._raw?.lastAt || "",
        },
        {
          id: "notes",
          label: "Notes",
          sortValue: (row) => personNotes(row._raw?.key).length,
        },
      ];

  const exitByLabel = (row) => {
    if (!(row.left || row.dropped)) return "";
    return getLeftByDisplay(row);
  };

  const tableRows = filtered.map((c) => {
    const meta = ACTIVITY_META[c.activity] || ACTIVITY_META.unknown;
    const verdict = VERDICT_META[c.performance?.verdict] || VERDICT_META.none;
    const VerdictIcon = verdict.Icon;
    const primary = c.performance?.primary;
    const leftByLabel = exitByLabel(c);
    const personCell = (
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: isBmsMemberSource(c.source) ? "#8b5cf6" : "#04abbb",
          }}
        >
          {getInitials(c.name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
            {c.name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            {c.email ||
              (isBmsMemberSource(c.source)
                ? "Public / BMS"
                : "GymRite member")}
          </Typography>
        </Box>
      </Stack>
    );

    if (isLead) {
      return {
        id: c.key,
        ...c,
        _raw: c,
        person: personCell,
        status: (
          <Label variant="ghost" color="info">
            {c.statusName || "Lead"}
          </Label>
        ),
        coach: (
          <Typography sx={{ fontSize: 13 }}>{c.coachName || "—"}</Typography>
        ),
        source: (
          <Typography sx={{ fontSize: 13 }}>
            {isBmsMemberSource(c.source) ? "Public form" : "GymRite member"}
          </Typography>
        ),
        submitted: (
          <Box>
            <Typography sx={{ fontSize: 13 }}>
              {c.lastAt ? moment(c.lastAt).format("DD MMM YYYY") : "—"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: MUTED }}>
              {c.lastAt ? moment(c.lastAt).fromNow() : ""}
            </Typography>
          </Box>
        ),
      };
    }

    return {
      id: c.key,
      ...c,
      _raw: c,
      person: personCell,
      performance: (
        <Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.4,
              px: 0.85,
              py: 0.3,
              borderRadius: "999px",
              bgcolor: verdict.bg,
              color: verdict.color,
              mb: primary ? 0.4 : 0,
            }}
          >
            <VerdictIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>
              {verdict.label}
            </Typography>
          </Box>
          {primary ? (
            <Typography sx={{ fontSize: 12, color: MUTED }}>
              {primary.label}: {primary.currentText}{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, color: verdict.color }}
              >
                ({primary.deltaText})
              </Box>
            </Typography>
          ) : null}
        </Box>
      ),
      activity: (
        <Box>
          <Label variant="ghost" color={meta.color}>
            {meta.label}
          </Label>
          {leftByLabel ? (
            <Typography sx={{ mt: 0.35, fontSize: 11.5, color: MUTED }}>
              by {leftByLabel}
            </Typography>
          ) : null}
        </Box>
      ),
      progress: (
        <Box sx={{ minWidth: 100 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontSize: 11, color: MUTED }}>
              {c.doneSteps != null && c.totalSteps
                ? `${c.doneSteps}/${c.totalSteps}`
                : `${c.formsCount} forms`}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
              {c.progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={c.progress}
            sx={{
              mt: 0.4,
              height: 6,
              borderRadius: 99,
              bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                borderRadius: 99,
                bgcolor: c.progress >= 70 ? "#10b981" : PRIMARY,
              },
            }}
          />
        </Box>
      ),
      coach: (
        <Typography sx={{ fontSize: 13 }}>{c.coachName || "—"}</Typography>
      ),
      entries: (
        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
          {c.submissions}
        </Typography>
      ),
      last: (
        <Box>
          <Typography sx={{ fontSize: 13 }}>
            {c.lastAt ? moment(c.lastAt).format("DD MMM YYYY") : "—"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: MUTED }}>
            {c.daysSince === 0 ? "Today" : `${c.daysSince}d ago`}
          </Typography>
        </Box>
      ),
    };
  });

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            {isLead ? "Leads" : "Members on this journey"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            {isLead
              ? "Everyone who submitted the lead form — click a row for detail."
              : "Drop out applies to the person. Exits show whether it was by Self, Coach, or Admin."}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}
        >
          {!isLead ? (
            <button
              type="button"
              className="small-contained-outlined-button"
              style={{ padding: "8px 14px", whiteSpace: "nowrap" }}
              onClick={() => {
                setReportInitialMembers([]);
                setReportOpen(true);
              }}
            >
              Progress report
            </button>
          ) : null}
          <TextField
            size="small"
            placeholder={isLead ? "Search leads…" : "Search members…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", sm: 260 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
        {isLead ? (
          <>
            <StatCard label="Leads" value={clients.length} hint="Unique" />
            <StatCard
              label="Submissions"
              value={kpis.submissions || 0}
              hint="All forms"
            />
          </>
        ) : (
          <>
            <StatCard label="Members" value={clients.length} hint="Unique" />
            <StatCard
              label="Improving"
              value={kpis.improvingPeople || 0}
              hint="vs baseline"
              tone="#047857"
            />
            <StatCard
              label="Declining"
              value={kpis.decliningPeople || 0}
              hint="needs focus"
              tone={kpis.decliningPeople ? "#b91c1c" : undefined}
            />
          </>
        )}
      </Box>

      <SubmissionNotesDialog
        open={Boolean(notesPerson)}
        onClose={() => setNotesPerson(null)}
        title={notesPerson ? `Notes · ${notesPerson.name || "Member"}` : ""}
        notes={notesPerson ? personNotes(notesPerson.key) : []}
        onChanged={reloadNotes}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: PRIMARY }} />
        </Box>
      ) : (
        <CustomMUITable
          TABLE_HEAD={TABLE_HEAD}
          data={tableRows.map((row) => ({
            ...row,
            notes: (
              <NotesCountButton
                count={personNotes(row.key).length}
                onClick={() => setNotesPerson({ key: row.key, name: row.name })}
              />
            ),
            MENU_OPTIONS: [
              row.latestSubmissionId
                ? {
                    label: "View",
                    icon: "akar-icons:eye",
                    handleClick: () =>
                      navigate(
                        `/tracker-submissions/${row.latestSubmissionId}`
                      ),
                  }
                : null,
              row.left || row.dropped
                ? null
                : {
                    label: "Check-in for member",
                    icon: "mdi:clipboard-edit-outline",
                    handleClick: () => setCheckInPerson(row),
                  },
              row.left || row.dropped || isLead
                ? null
                : {
                    label: "Progress report",
                    icon: "mdi:file-chart-outline",
                    handleClick: () => {
                      setReportInitialMembers([row]);
                      setReportOpen(true);
                    },
                  },
              row.left || row.dropped || !row.latestSubmissionId
                ? null
                : {
                    label: "Drop out",
                    icon: "mdi:account-remove-outline",
                    handleClick: () => setDropPerson(row),
                  },
            ].filter(Boolean),
          }))}
          MENU_OPTIONS={[]}
          className="card-with-background tracker-module-table"
          hide_search
          sortable
          onRowClick={(row) => {
            if (row.latestSubmissionId) {
              navigate(`/tracker-submissions/${row.latestSubmissionId}`, { state: withBack() });
            }
          }}
        />
      )}

      <DropOutPersonDialog
        open={Boolean(dropPerson)}
        person={dropPerson}
        trackerName={trackerName}
        onClose={() => setDropPerson(null)}
        onDropped={async () => {
          setDropPerson(null);
          await reloadPeople();
        }}
      />

      <StaffCheckInDialog
        open={Boolean(checkInPerson)}
        tracker={tracker}
        person={checkInPerson}
        personSubmissions={
          checkInPerson
            ? submissions.filter((row) => {
                const key = getSubmissionPersonKey(row);
                return (
                  key &&
                  (key === checkInPerson.key ||
                    key === checkInPerson.personKey ||
                    (checkInPerson.email &&
                      String(row.email || "").toLowerCase() ===
                        String(checkInPerson.email).toLowerCase()) ||
                    (checkInPerson.memberId &&
                      String(row.memberId || "") ===
                        String(checkInPerson.memberId)))
                );
              })
            : []
        }
        onClose={() => setCheckInPerson(null)}
        onSaved={async () => {
          const person = checkInPerson;
          setCheckInPerson(null);
          await reloadPeople();
          if (person && !isLead) {
            setReportInitialMembers([person]);
            setReportOpen(true);
          }
        }}
      />

      <CoachProgressReportDialog
        open={reportOpen}
        tracker={tracker}
        people={clients}
        initialMembers={reportInitialMembers}
        onClose={() => {
          setReportOpen(false);
          setReportInitialMembers([]);
        }}
      />
    </Box>
  );
}
